import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { SubprinciplesConfigGrid } from 'src/screens/administration/configuration-management/principles-configuration/subprinciples/SubprinciplesConfigGrid'
import { useCallback, useEffect, useState } from 'react'
import { ConfigurationTableSettings, DEFAULT_TABLE_SETTINGS } from 'src/shared/constants/table-settings-constants'
import { useClient } from 'urql'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { Query_Root } from 'src/@types/graphql'
import { querySubprincipleConfigs } from 'src/screens/administration/configuration-management/principles-configuration/subprinciples/subprinciplesConfigQueries'
import { useUserLocale } from 'src/user/UserContext'

export const PrinciplesConfigurationSubprinciplesPage = () => {
  const { getMessage } = useMessageSource()
  const urqlClient = useClient()
  const notificationService = useNotificationService()
  const locale = useUserLocale()

  const [tableSettings, setTableSettings] = useState<ConfigurationTableSettings>(DEFAULT_TABLE_SETTINGS)

  const fetchRows = useCallback(async () => {
    const limit = tableSettings.paginationModel.pageSize
    const offset = tableSettings.paginationModel.page * tableSettings.paginationModel.pageSize

    const { data, error } = await urqlClient
      .query<
        {
          subprinciple_config: Query_Root['subprinciple_config']
          subprinciple_config_aggregate: Query_Root['subprinciple_config_aggregate']
        },
        {
          offset: number
          limit: number
        }
      >(querySubprincipleConfigs, {
        offset,
        limit,
      })
      .toPromise()

    if (error || !data?.subprinciple_config || !data.subprinciple_config_aggregate) {
      throw new Error('Resource not found')
    }

    const fetchedRows = data.subprinciple_config.map((spc) => ({
      id: spc.id,
      name: spc.subprinciple_config_name_locale,
      status: spc.active,
      parent_principle: spc.principle_config.names[locale],
    }))
    const fetchedRowsCount = data.subprinciple_config_aggregate.aggregate?.count ?? 0

    return {
      fetchedRows,
      fetchedRowsCount,
    }
  }, [tableSettings.paginationModel, locale, urqlClient])

  useEffect(() => {
    setTableSettings((prevSettings) => ({ ...prevSettings, gridLoading: true }))
    fetchRows()
      .then((res) => {
        setTableSettings((prevSettings) => ({
          ...prevSettings,
          rows: res.fetchedRows,
          rowsCount: res.fetchedRowsCount,
        }))
      })
      .catch((_) => {
        notificationService.operationFailed()
      })
      .finally(() => {
        setTableSettings((prevSettings) => ({ ...prevSettings, gridLoading: false }))
      })
  }, [fetchRows, notificationService])

  return (
    <ScreenLayout
      title={`${getMessage('label.navigation.configuration.principles')} ${getMessage('label.configuration')}`}
    >
      <PageLayout>
        <SubprinciplesConfigGrid tableSettings={tableSettings} setTableSettings={setTableSettings} />
      </PageLayout>
    </ScreenLayout>
  )
}
